import React, { useCallback, useEffect, useRef, useState } from 'react';
import FlowHandlingButtons from '../Common/HandleNavigationAndSubmit';
import { contentIsNotNull } from '../Common/Validator';
import { spaceListingData } from '../Common/ProfileListingDataModal';
import { getProfileData, setProfileData } from '../Common/DataController';
import { DEFAULT_DATA_OBJ } from '../SpaceListingConstants';
import { Col, Row, Icon, Spin, message } from 'antd';
import { useParams } from 'react-router-dom';
import ImageEnhanceCompareModal from './ImageEnhanceCompareModal';
import ImagePreviewModal from './ImagePreviewModal';
import {
  enhanceImageFile,
  fetchImageAsFile,
  uploadEnhancedImageToS3,
} from '../../../utils/imageEnhanceUtils';
import { uploadImage, deleteImage, getImageUrl, generateObjectId } from '../../../utils/imageUploadService';

const ENHANCE_MODAL_IDLE = 'idle';
const ENHANCE_MODAL_LOADING = 'loading';
const ENHANCE_MODAL_COMPARE = 'compare';
const ENHANCE_MODAL_UPLOADING = 'uploading';
const ENHANCE_ERROR_MESSAGE = "We couldn't enhance this image right now. Please check your connection and try again.";

const getEnhanceErrorMessage = (error) => {
  const message = error?.message;
  if (!message || message === 'Failed to fetch') {
    return ENHANCE_ERROR_MESSAGE;
  }
  return message;
};

const UploadImages = ({ currentBlock }) => {
  const { country, listingId } = useParams();
  const [uploadImageDetails, setUploadImageDetails] = useState(getProfileData()?.[DEFAULT_DATA_OBJ] ? getProfileData() : spaceListingData(country));
  const [isNextBtnActive, setIsNextBtnActive] = useState(false);
  const [imageError, setImageError] = useState('');
  const [imageWarning, setImageWarning] = useState('');
  const [primaryError, setPrimaryError] = useState('');
  const [enhancingAssetId, setEnhancingAssetId] = useState(null);

  // Track individual file upload progress
  const [uploadingFiles, setUploadingFiles] = useState({});

  const [enhanceModal, setEnhanceModal] = useState({
    visible: false,
    phase: ENHANCE_MODAL_IDLE,
    error: null,
    assetId: null,
    originalUrl: null,
    enhancedPreviewUrl: null,
  });

  const enhancedPreviewUrlRef = useRef(null);
  const enhancedBlobRef = useRef(null);
  const [previewImage, setPreviewImage] = useState(null);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const revokeEnhancedPreview = useCallback(() => {
    if (enhancedPreviewUrlRef.current) {
      URL.revokeObjectURL(enhancedPreviewUrlRef.current);
      enhancedPreviewUrlRef.current = null;
    }
    enhancedBlobRef.current = null;
  }, []);

  useEffect(() => {
    return () => revokeEnhancedPreview();
  }, [revokeEnhancedPreview]);

  // Auto-clear image warning after 8 seconds
  useEffect(() => {
    let timer;
    if (imageWarning) {
      timer = setTimeout(() => {
        setImageWarning('');
      }, 8000);
    }
    return () => clearTimeout(timer);
  }, [imageWarning]);

  useEffect(() => {
    const isImages = getUploadedImages().filter((key) => key?.resource_type !== 'video');
    const selectedImage = getFieldCurrentValue('selectedImage');

    if (isImages.length > 10) {
      setImageError('You can upload a maximum of 10 images. Please remove some images to continue.');
      setIsNextBtnActive(false);
      return;
    }

    setImageError('');
    const hasMinImages = contentIsNotNull(isImages, 0, 10);

    if (hasMinImages && !selectedImage) {
      setPrimaryError('Please select a cover photo before continuing.');
      setIsNextBtnActive(false);
    } else {
      setPrimaryError('');
      setIsNextBtnActive(hasMinImages);
    }
  }, [uploadImageDetails]);

  const getUploadedImages = () => {
    return uploadImageDetails?.[currentBlock]?.[0]?.uploadedImages || [];
  };

  const getReferenceId = () => {
    if (listingId) return listingId;
    const profile = getProfileData();
    if (profile?._id) return profile._id;
    if (profile?.imageReferenceId) return profile.imageReferenceId;

    const newRefId = generateObjectId();
    const tempObj = {
      ...(profile || uploadImageDetails || spaceListingData(country)),
      imageReferenceId: newRefId,
    };
    setProfileData(tempObj);
    setUploadImageDetails(tempObj);
    return newRefId;
  };

  const persistUploadState = (mutator) => {
    const profile = getProfileData() || uploadImageDetails || spaceListingData(country);
    const blocks = profile[currentBlock] || [{ selectedImage: {}, uploadedImages: [] }];

    const updatedBlocks = blocks.map((block) => {
      const next = {
        ...block,
        uploadedImages: Array.isArray(block.uploadedImages) ? [...block.uploadedImages] : [],
      };
      mutator(next);
      return next;
    });

    const tempObj = {
      ...profile,
      [currentBlock]: updatedBlocks,
    };

    setProfileData(tempObj);
    setUploadImageDetails(tempObj);
  };

  const changeSelectedImage = (newValue) => {
    if (newValue) {
      persistUploadState((block) => {
        block.selectedImage = newValue;
      });
    }
  };

  const removeSelectedImage = async (id) => {
    persistUploadState((block) => {
      block.uploadedImages = block.uploadedImages.filter((img) => img?.asset_id !== id);
      if (block.selectedImage === id) {
        block.selectedImage = block.uploadedImages.find((img) => img?.resource_type === 'image')?.asset_id;
      }
    });

    // Optionally call API to delete from S3
    try {
      await deleteImage(id);
    } catch (e) {
      console.warn('Failed to delete image from server', e);
    }
  };

  const removeSelectedVideo = async (id) => {
    persistUploadState((block) => {
      block.uploadedImages = block.uploadedImages.filter((img) => img?.asset_id !== id);
    });

    try {
      await deleteImage(id);
    } catch (e) {
      console.warn('Failed to delete video from server', e);
    }
  };

  const replaceUploadedImage = (oldAssetId, newImageInfo) => {
    persistUploadState((block) => {
      block.uploadedImages = block.uploadedImages.map((img) =>
        img?.asset_id === oldAssetId
          ? { ...newImageInfo, aiEnhanced: true }
          : img
      );
      if (block.selectedImage === oldAssetId) {
        block.selectedImage = newImageInfo.asset_id;
      }
    });
  };

  const getFieldCurrentValue = (type) => {
    return uploadImageDetails?.[currentBlock]?.[0]?.[type];
  };

  const closeEnhanceModal = useCallback(() => {
    revokeEnhancedPreview();
    setEnhancingAssetId(null);
    setEnhanceModal({
      visible: false,
      phase: ENHANCE_MODAL_IDLE,
      error: null,
      assetId: null,
      originalUrl: null,
      enhancedPreviewUrl: null,
    });
  }, [revokeEnhancedPreview]);

  const runEnhance = async (assetId, resolvedUrl) => {
    revokeEnhancedPreview();
    setEnhancingAssetId(assetId);
    setEnhanceModal({
      visible: true,
      phase: ENHANCE_MODAL_LOADING,
      error: null,
      assetId,
      originalUrl: resolvedUrl,
      enhancedPreviewUrl: null,
    });

    try {
      const file = await fetchImageAsFile(resolvedUrl, `space-${assetId}.jpg`);
      const enhancedBlob = await enhanceImageFile(file);
      enhancedBlobRef.current = enhancedBlob;
      const previewUrl = URL.createObjectURL(enhancedBlob);
      enhancedPreviewUrlRef.current = previewUrl;

      setEnhancingAssetId(null);
      setEnhanceModal((prev) => ({
        ...prev,
        phase: ENHANCE_MODAL_COMPARE,
        enhancedPreviewUrl: previewUrl,
        error: null,
      }));
    } catch (err) {
      setEnhancingAssetId(null);
      setEnhanceModal((prev) => ({
        ...prev,
        phase: ENHANCE_MODAL_IDLE,
        error: getEnhanceErrorMessage(err),
      }));
    }
  };

  const startEnhance = async (imageInfo, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (enhancingAssetId) return;

    const assetId = imageInfo?.asset_id;
    const imageUrl = imageInfo?.url;
    if (!assetId || !imageUrl) return;

    const resolvedUrl = imageInfo.publicUrl || (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('blob:') && !imageUrl.startsWith('data:') ? getImageUrl(imageUrl) : imageUrl);

    runEnhance(assetId, resolvedUrl);
  };

  const retryEnhance = () => {
    const { assetId, originalUrl } = enhanceModal;
    if (!assetId || !originalUrl || enhancingAssetId) return;

    runEnhance(assetId, originalUrl);
  };

  const handleKeepOriginal = () => {
    closeEnhanceModal();
  };

  const handleUseEnhanced = async () => {
    const { assetId, enhancedPreviewUrl } = enhanceModal;
    if (!assetId || !enhancedPreviewUrl) return;

    setEnhanceModal((prev) => ({ ...prev, phase: ENHANCE_MODAL_UPLOADING, error: null }));

    try {
      const blob = enhancedBlobRef.current;
      if (!blob) {
        throw new Error('Enhanced image is no longer available. Please try again.');
      }
      const s3Info = await uploadEnhancedImageToS3(blob, `enhanced-${assetId}.png`, 'spaces', getReferenceId());
      replaceUploadedImage(assetId, s3Info);
      closeEnhanceModal();
    } catch (err) {
      setEnhanceModal((prev) => ({
        ...prev,
        phase: ENHANCE_MODAL_COMPARE,
        error: err.message || 'Failed to save enhanced image. Please try again.',
      }));
    }
  };

  const handleFileUpload = async (files, isVideo = false) => {
    if (!files || files.length === 0) return;

    setImageWarning(''); // Clear any existing warning
    const referenceId = getReferenceId();
    const currentImages = getUploadedImages();

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uploadId = `upload-${Date.now()}-${i}`;

      setUploadingFiles((prev) => ({
        ...prev,
        [uploadId]: { file, progress: 0, isVideo },
      }));

      try {
        const isPrimary = !isVideo && currentImages.length === 0 && i === 0;
        const result = await uploadImage(file, 'spaces', referenceId, {
          onProgress: (percent) => {
            setUploadingFiles((prev) => ({
              ...prev,
              [uploadId]: { ...prev[uploadId], progress: percent },
            }));
          },
          isPrimary,
          displayOrder: currentImages.length + i,
          onWarning: (warnMsg) => setImageWarning(warnMsg),
        });

        persistUploadState((block) => {
          block.uploadedImages = [...block.uploadedImages, result];
          // Auto-set first uploaded image as cover
          if (isPrimary && !block.selectedImage) {
            block.selectedImage = result.asset_id;
            setPrimaryError('');
          }
        });
      } catch (error) {
        message.error(`Failed to upload ${file.name}: ${error.message}`);
      } finally {
        setUploadingFiles((prev) => {
          const updated = { ...prev };
          delete updated[uploadId];
          return updated;
        });
      }
    }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    handleFileUpload(files, false);
  };

  const handleVideoDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('video/'));
    handleFileUpload(files, true);
  };

  const selectImage = (asset_id) => {
    changeSelectedImage(asset_id);
    setPrimaryError('');
  };

  const openImagePreview = (imageInfo, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (!imageInfo?.url) return;
    setPreviewImage({
      url: imageInfo.publicUrl || (imageInfo.url && !imageInfo.url.startsWith('http') && !imageInfo.url.startsWith('blob:') && !imageInfo.url.startsWith('data:') ? getImageUrl(imageInfo.url) : imageInfo.url),
      title: imageInfo.aiEnhanced ? 'AI-enhanced image' : 'Uploaded image',
    });
  };

  const closeImagePreview = () => {
    setPreviewImage(null);
  };

  const imageList = getUploadedImages().filter((value) => {
    if (['mp4', '3gp'].includes(value?.format) || value?.resource_type === 'video') return false;
    return value?.resource_type === 'image' || (value?.url && value?.resource_type !== 'video');
  });

  const videoList = getUploadedImages().filter((value) => {
    return ['mp4', '3gp'].includes(value?.format) || value?.resource_type === 'video';
  });

  const uploadingItems = Object.values(uploadingFiles);
  const uploadingImages = uploadingItems.filter(item => !item.isVideo);
  const uploadingVideos = uploadingItems.filter(item => item.isVideo);

  return (
    <>
      <div className="left-side-inner-wrapper upload-img-page">
        <h1 className="listing-page-title">Uploads</h1>
        <p className="listing-page-description">
          Upload your best images. The human brain processes visuals up to 60,000 times faster than text: the better the images, the more bookings you’ll get!
        </p>

        <div className="sl-upload-container">
          <Row gutter={32}>
            {/* Images Column */}
            <Col md={12} sm={24}>
              <div className="sl-upload-section">
                <h4 className="sl-upload-label">Images</h4>
                <div
                  className="sl-dropzone"
                  onClick={() => imageInputRef.current?.click()}
                  onDrop={handleImageDrop}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    type="file"
                    multiple
                    hidden
                    accept="image/jpeg, image/png, image/webp"
                    ref={imageInputRef}
                    onChange={(e) => handleFileUpload(e.target.files, false)}
                  />
                  <div className="sl-dropzone-inner">
                    <div className="sl-upload-icon-circle">
                      <Icon type="cloud-upload" />
                    </div>
                    <p className="sl-dropzone-text">
                      <span className="sl-upload-link">Upload or drag and drop</span>
                    </p>
                    <p className="sl-dropzone-hint">Supported formats: JPEG, PNG, WEBP</p>
                  </div>
                </div>
                <p className="sl-resolution-text">Minimum: 800 x 533px &nbsp;|&nbsp; Recommended: 1024 x 683px or higher</p>
                <p className="sl-resolution-text">Upload 1 to 10 images. One must be selected as the cover photo.</p>
                {imageWarning && (
                  <p className="sl-image-warning-text">
                    {imageWarning}
                  </p>
                )}
                {imageError && (
                  <p className="sl-image-error-text">
                    {imageError}
                  </p>
                )}
                {primaryError && (
                  <p className="sl-image-error-text">
                    {primaryError}
                  </p>
                )}

                <div className="sl-preview-grid">
                  <Row gutter={[8, 8]}>
                    {/* Uploading Images */}
                    {uploadingImages.map((item, idx) => (
                      <Col span={8} key={`uploading-img-${idx}`}>
                        <div className="sl-preview-item">
                          <div className="sl-preview-card sl-uploading-card">
                            <div className="sl-uploading-card-inner">
                              <Spin percent={item.progress} size="small" />
                              <div className="sl-uploading-text">{item.progress}%</div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}

                    {/* Completed Images */}
                    {imageList.map((value) => {
                      const isEnhancing = enhancingAssetId === value?.asset_id;
                      const isPrimary = value?.asset_id === getFieldCurrentValue('selectedImage');
                      return (
                        <Col span={8} key={value?.asset_id}>
                          <div className="sl-preview-item">
                            <div className={`sl-preview-card ${isPrimary ? 'is-primary' : ''}`}>
                              {isPrimary && (
                                <span className="sl-primary-badge">Cover</span>
                              )}
                              <div
                                className="sl-preview-media"
                                onClick={(e) => openImagePreview(value, e)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    openImagePreview(value, e);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label="View image"
                              >
                                <img src={value.publicUrl || (value.url && !value.url.startsWith('http') && !value.url.startsWith('blob:') && !value.url.startsWith('data:') ? getImageUrl(value.url) : value.url)} alt="preview" />
                                {value?.aiEnhanced && <span className="sl-ai-badge">AI</span>}
                                {isEnhancing && (
                                  <div className="sl-preview-enhancing">
                                    <Spin size="small" />
                                  </div>
                                )}
                              </div>
                              <div className="sl-preview-overlay">
                                <Icon
                                  type="eye"
                                  className="sl-preview-view-hint"
                                  onClick={(e) => openImagePreview(value, e)}
                                />
                                <Icon
                                  type="delete"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeSelectedImage(value?.asset_id);
                                  }}
                                />
                                {isPrimary ? (
                                  <Icon type="check-circle" theme="filled" />
                                ) : (
                                  <Icon
                                    type="star"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      selectImage(value?.asset_id);
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                            {!value?.aiEnhanced && (
                              <button
                                type="button"
                                className="sl-ai-enhance-btn"
                                disabled={!!enhancingAssetId || enhanceModal.visible}
                                onClick={(e) => startEnhance(value, e)}
                              >
                                <Icon type="rocket" />
                                Enhance with AI
                              </button>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              </div>
            </Col>

            {/* Videos Column */}
            <Col md={12} sm={24}>
              <div className="sl-upload-section">
                <h4 className="sl-upload-label">Videos</h4>
                <div
                  className="sl-dropzone"
                  onClick={() => videoInputRef.current?.click()}
                  onDrop={handleVideoDrop}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    type="file"
                    multiple
                    hidden
                    accept="video/mp4, video/3gpp, video/quicktime"
                    ref={videoInputRef}
                    onChange={(e) => handleFileUpload(e.target.files, true)}
                  />
                  <div className="sl-dropzone-inner">
                    <div className="sl-upload-icon-circle">
                      <Icon type="cloud-upload" />
                    </div>
                    <p className="sl-dropzone-text">
                      <span className="sl-upload-link">Upload or drag and drop</span>
                    </p>
                    <p className="sl-dropzone-hint">Supported formats: MP4, 3GP, MOV</p>
                  </div>
                </div>
                <p className="sl-resolution-text">Recommended: 1280 x 720px (HD) or higher &nbsp;|&nbsp; Max duration: 3 min &nbsp;|&nbsp; Max size: 100 MB</p>

                <div className="sl-preview-grid">
                  <Row gutter={[8, 8]}>
                    {/* Uploading Videos */}
                    {uploadingVideos.map((item, idx) => (
                      <Col span={8} key={`uploading-vid-${idx}`}>
                        <div className="sl-preview-item">
                          <div className="sl-preview-card sl-uploading-card">
                            <div className="sl-uploading-card-inner">
                              <Spin percent={item.progress} size="small" />
                              <div className="sl-uploading-text">{item.progress}%</div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}

                    {/* Completed Videos */}
                    {videoList.map((value) => (
                      <Col span={8} key={value?.asset_id}>
                        <div className="sl-preview-card is-video">
                          <video controls preload="metadata" src={value.publicUrl || (value.url && !value.url.startsWith('http') && !value.url.startsWith('blob:') && !value.url.startsWith('data:') ? getImageUrl(value.url) : value.url)} />
                          <div className="sl-preview-overlay" style={{ pointerEvents: 'none' }}>
                            <Icon type="delete" style={{ pointerEvents: 'auto' }} onClick={(e) => { e.stopPropagation(); removeSelectedVideo(value?.asset_id); }} />
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <ImagePreviewModal
        visible={!!previewImage}
        imageUrl={previewImage?.url}
        title={previewImage?.title}
        onClose={closeImagePreview}
      />

      <ImageEnhanceCompareModal
        visible={enhanceModal.visible}
        phase={enhanceModal.phase}
        error={enhanceModal.error}
        originalUrl={enhanceModal.originalUrl}
        enhancedPreviewUrl={enhanceModal.enhancedPreviewUrl}
        onKeepOriginal={handleKeepOriginal}
        onUseEnhanced={handleUseEnhanced}
        onRetry={retryEnhance}
        onClose={closeEnhanceModal}
      />

      <FlowHandlingButtons isActive={isNextBtnActive} />
    </>
  );
};

export default UploadImages;
